/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCallback, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import { StyledSelect } from '../Sidebar/StyledSelect';
import CircularProgress from '@mui/material/CircularProgress';
import { useDispatch, useSelector } from 'react-redux';
import { IState } from '../../reducers';
import { IChartItem, IInitialResultChartData } from '../../model/IInitialChartData';
import { IChartBullet } from '../../model/IChartData';
import Chart from './Chart';
import { useIntl } from 'react-intl';
import mikeSharedTheme from '../../styles/mikeSharedTheme';
import { Feature, Point } from 'geojson';
import { resultChartDataGet, setResultChartAxisRange, setSelectedResultPointName } from '../../actions/projectContent';
import { useParams } from 'react-router-dom';

const selectContainerStyle = css`
  padding-right: ${mikeSharedTheme.spacing(1)};
  padding-left: ${mikeSharedTheme.spacing(1)};
  display: flex;
  align-items: center;
`
const labelStyle = css`
  padding: ${mikeSharedTheme.spacing(1)};
  white-space: nowrap;
  padding-bottom: ${mikeSharedTheme.spacing(1)}; 
`
const placeHolderStyle= css`
  width: ${mikeSharedTheme.spacing(8)};  
`
const spinnerContainerStyle = css`  
  align-items: center;
  justify-content: center;
`

const flexStyle = css`
  display: flex;  
  align-items: center; 
`
const spaceBetweenStyle = css`
  display: flex; 
  justify-content: space-between; 
  align-items: center;
  width: ${mikeSharedTheme.spacing(98)};   
`

const VALUEFIELD = "id";
const PRIMARYFIELD ="id";
const NAMEFIELD = "id";
const XFIELD = "x";
const YFIELD = "y";

const ResultChartContainer = () => {  
  const { id } = useParams();  
  const dispatch = useDispatch();
  const intl = useIntl();

  const points: Array<Feature<Point>>  = useSelector(
    (state: IState) => state.mapContent.points
  ); 

  const selectedXItem: IChartItem  = useSelector(
    (state: IState) => state.projectContent.selectedChartXItem
  ); 

  const selectedYItem: IChartItem  = useSelector(
    (state: IState) => state.projectContent.selectedChartYItem
  ); 

  const previewData: IInitialResultChartData = useSelector(
    (state: IState) => state.projectContent.resultInitialChartData
  ); 

  const selectedResultPointName: string = useSelector(
    (state: IState) => state.projectContent.selectedResultPointName
  ); 

  const chartBullets: Array<IChartBullet>  = useSelector(
    (state: IState) => state.projectContent.chartBullets
  ); 

  const loadingChartData: boolean  = useSelector(
    (state: IState) => state.projectContent.loadingResultInitialChartData
  ); 

 const chartAxisRange = useSelector(
  (state: IState) => state.projectContent.chartAxisRange
); 

/*const jobs: Array<IJob> = useSelector(
  (state: IState) => state.job.jobs
);  


 const jobIsRunning = useMemo(() => {
  const job = getJob(jobs, JOBS.AUTOSETUP)
  const jobStatus = job !== undefined && job.status ? job.status : ""
  return jobStatus && JOBS_IN_PROGRESS.includes(jobStatus.toLowerCase())    
}, [jobs]) */

const axisLabels = useMemo(() => {
  const xUnit = selectedXItem.unit ? " (" + selectedXItem.unit + ")" : ""
  const yUnit = selectedYItem.unit ? " (" + selectedYItem.unit + ")"  : ""
  return [selectedXItem.displayName + xUnit, selectedYItem.displayName + yUnit]
}, [selectedXItem, selectedYItem])
  const handleChangeChartAxisRange = useCallback((axisRange) => {   
    dispatch(setResultChartAxisRange(axisRange))
  }, [dispatch]) 

  const handleChangePointName = (event: any) => { 
    dispatch(setResultChartAxisRange(null)) 
    dispatch(setSelectedResultPointName(event.target.value))
  }

  const handleChangeXAxis = useCallback((event: any) => {   
    dispatch(setResultChartAxisRange(null))   
    const newXId = event.target.value
    const newXItem = previewData.parameters.find(d => d.id === newXId)
    dispatch(resultChartDataGet(id, newXItem, selectedYItem))
  }, [dispatch, id, previewData.parameters, selectedYItem])

  const handleChangeYAxis = useCallback((event: any) => {   
    dispatch(setResultChartAxisRange(null)) 
    const newYId = event.target.value
    const newYItem = previewData.parameters.find(d => d.id === newYId)
    dispatch(resultChartDataGet(id, selectedXItem, newYItem))
  }, [dispatch, id, previewData, selectedXItem]) 

  if (!selectedResultPointName){
    return null
  }


  return (    
    loadingChartData ? 
      <div css={spinnerContainerStyle}><CircularProgress/></div>:       
        previewData && previewData.parameters ? <>
      <div css={selectContainerStyle}>
        <div css={spaceBetweenStyle}>
          <div css={flexStyle}>
            <Typography variant="body2" css={labelStyle}>{intl.formatMessage({id: 'chartContainer.selectBoundaryOrForcing'}) + ":"}</Typography>  
            <StyledSelect
              fullWidth={false}
              name={NAMEFIELD}
              value={selectedResultPointName}         
              valueField={VALUEFIELD}
              primaryField={PRIMARYFIELD}
              items={points}
              onChange={handleChangePointName}
            />
          </div>
        </div>          
      </div> 
      <div css={selectContainerStyle}>       
        <Typography variant="body2" css={labelStyle}>{intl.formatMessage({id: 'chartContainer.axisConfig'})}</Typography>
        <div css={placeHolderStyle}/>
        <Typography variant="body2" css={labelStyle}>{intl.formatMessage({id: 'chartContainer.xLabel'}) + ":"}</Typography>
        <StyledSelect
          fullWidth={true}
          name={XFIELD}
          value={selectedXItem && selectedXItem.id ? selectedXItem.id : undefined}         
          valueField={VALUEFIELD}
          primaryField={PRIMARYFIELD}
          items={previewData.parameters}
          onChange={handleChangeXAxis}
        />
        <Typography variant="body2" css={labelStyle}>{intl.formatMessage({id: 'chartContainer.yLabel'}) + ":"}</Typography>
        <StyledSelect
          fullWidth={true}
          name={YFIELD}
          value={selectedYItem && selectedYItem.id ? selectedYItem.id : undefined}         
          valueField={VALUEFIELD}
          primaryField={PRIMARYFIELD}
          items={previewData.parameters}
          onChange={handleChangeYAxis}
        />
      </div>
      {selectedXItem && selectedXItem.id && previewData.axises && previewData.axises.length > 1 && selectedYItem &&    
      <Chart 
        title={'<b>Model run results at selected point</b>'}
        onChangeAxisRange={handleChangeChartAxisRange} 
        xIsTimeSeries={selectedXItem.id === previewData.axises[0].id}
        yIsTimeSeries={selectedYItem.id === previewData.axises[0].id}
        axisLabels={axisLabels} 
        data={chartBullets}
        chartAxisRange={chartAxisRange}
      /> }

    </> : null
  );
};

export default ResultChartContainer;